import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Fade, IconButton, Stack, TextField, Typography } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { format, isSameDay } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { useAuth } from '../../shared/hooks/useAuth';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { useApiError } from '../../shared/errors/useApiError';
import type { ChatMessage } from '../../shared/services/chat.service';
import { useConversation } from './useConversation';

type Props = {
  conversationId: string;
  /** Header label shown at the top of the box (e.g. the other participant's name). */
  title?: string;
  subtitle?: string;
};

/**
 * Self-contained chat panel — message list (auto-scrolling to the bottom) +
 * input row. Renders inside whatever container the caller provides (drawer,
 * inbox split-pane, embedded card).
 */
export function ChatBox({ conversationId, title, subtitle }: Props) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;
  const { user } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { messages, isLoading, sendMessage, isSending } = useConversation(conversationId);

  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const prevCountRef = useRef(0);
  const hasMountedRef = useRef(false);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // ChatGPT-style auto-scroll: only follow new messages when the user is already
  // at the bottom (or just sent the message themselves). If they've scrolled up
  // to read history, we don't yank them back down — the floating button does.
  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = listRef.current;
    if (!el) return;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAtBottom(distanceFromBottom < 80);
  }, []);

  useLayoutEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = messages.length;

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      scrollToBottom(false);
      return;
    }
    if (messages.length <= prev) return;

    const last = messages[messages.length - 1];
    const lastIsMine = last?.senderUserId === user?.id;
    if (isAtBottom || lastIsMine) {
      scrollToBottom(true);
    }
  }, [messages, isAtBottom, scrollToBottom, user?.id]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || isSending) return;
    try {
      await sendMessage(body);
      setDraft('');
    } catch (err) {
      notify.error(translate(err));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Enter sends, Shift+Enter inserts a line break.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      {(title || subtitle) && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flex: '0 0 auto',
          }}
        >
          {title && <Typography sx={{ fontWeight: 700 }}>{title}</Typography>}
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <Box
          ref={listRef}
          onScroll={handleScroll}
          sx={{
            height: '100%',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            scrollBehavior: 'smooth',
            px: 2,
            py: 2,
            bgcolor: 'background.default',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('chat.empty')}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const showDateSeparator =
                !prev || !isSameDay(new Date(prev.sentAt), new Date(m.sentAt));
              const isMine = m.senderUserId === user?.id;
              return (
                <Box key={m.id}>
                  {showDateSeparator && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        textAlign: 'center',
                        textTransform: 'capitalize',
                        my: 1,
                      }}
                    >
                      {format(new Date(m.sentAt), 'EEEE d MMMM', {
                        locale: dateLocale,
                      })}
                    </Typography>
                  )}
                  <MessageBubble message={m} isMine={isMine} />
                </Box>
              );
            })}
          </Stack>
        )}
        </Box>

        <Fade in={!isAtBottom} unmountOnExit>
          <IconButton
            onClick={() => scrollToBottom(true)}
            size="small"
            aria-label={t('chat.scrollToBottom', 'Aller au dernier message')}
            sx={{
              position: 'absolute',
              right: 16,
              bottom: 16,
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <KeyboardArrowDownRoundedIcon />
          </IconButton>
        </Fade>
      </Box>

      <Box
        sx={{
          flex: '0 0 auto',
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={5}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.placeholder')}
            size="small"
            inputProps={{ maxLength: 4000 }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!draft.trim() || isSending}
            color="primary"
            sx={{
              bgcolor: 'primary.main',
              color: 'common.white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'text.disabled',
              },
            }}
            aria-label={t('chat.send')}
          >
            <SendRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    </Stack>
  );
}

function MessageBubble({ message, isMine }: { message: ChatMessage; isMine: boolean }) {
  const time = format(new Date(message.sentAt), 'HH:mm');
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isMine ? 'flex-end' : 'flex-start',
      }}
    >
      <Box
        sx={{
          maxWidth: '75%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: isMine ? 'primary.main' : 'action.hover',
          color: isMine ? 'common.white' : 'text.primary',
          boxShadow: isMine ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
        }}
      >
        <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {message.body}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'right',
            mt: 0.25,
            color: isMine ? 'rgba(255,255,255,0.75)' : 'text.secondary',
          }}
        >
          {time}
        </Typography>
      </Box>
    </Box>
  );
}
